/**
 * Axios HTTP Client
 *
 * Centralized axios instance with interceptors for:
 * - Authentication
 * - Error normalization
 * - Request tracing
 */

import axios from "axios"

import { env } from "@/shared/config/env"

import {
  requestErrorInterceptor,
  requestInterceptor,
  responseErrorInterceptor,
  responseInterceptor,
} from "./interceptors"

import type { ApiRequestConfig, ApiResponse, NormalizedError } from "./types"

/**
 * Create axios instance with default configuration
 */
const http = axios.create({
  baseURL: env.VITE_BACKEND_URL,
  timeout: env.VITE_API_TIMEOUT, // ms — VITE_API_TIMEOUT ile ayarlanır, gelmezse 30000
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
})

// Apply interceptors
http.interceptors.request.use(requestInterceptor, requestErrorInterceptor)
http.interceptors.response.use(responseInterceptor, responseErrorInterceptor)

/**
 * Type-safe GET request
 */
export async function get<T>(
  url: string,
  config?: ApiRequestConfig
): Promise<ApiResponse<T>> {
  const response = await http.get<T>(url, config)
  const data = response.data as unknown as { value?: T }
  return {
    data: data && typeof data === "object" && "value" in data ? (data.value as T) : response.data,
    status: response.status,
    statusText: response.statusText,
  }
}

/**
 * Type-safe POST request
 */
export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: ApiRequestConfig
): Promise<ApiResponse<T>> {
  const response = await http.post<T>(url, data, config)
  const responseData = response.data as unknown as { value?: T }
  return {
    data:
      responseData && typeof responseData === "object" && "value" in responseData
        ? (responseData.value as T)
        : response.data,
    status: response.status,
    statusText: response.statusText,
  }
}

/**
 * Type-safe PUT request
 */
export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: ApiRequestConfig
): Promise<ApiResponse<T>> {
  const response = await http.put<T>(url, data, config)
  const responseData = response.data as unknown as { value?: T }
  return {
    data:
      responseData && typeof responseData === "object" && "value" in responseData
        ? (responseData.value as T)
        : response.data,
    status: response.status,
    statusText: response.statusText,
  }
}

/**
 * Type-safe PATCH request
 */
export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: ApiRequestConfig
): Promise<ApiResponse<T>> {
  const response = await http.patch<T>(url, data, config)
  const responseData = response.data as unknown as { value?: T }
  return {
    data:
      responseData && typeof responseData === "object" && "value" in responseData
        ? (responseData.value as T)
        : response.data,
    status: response.status,
    statusText: response.statusText,
  }
}

/**
 * Type-safe DELETE request
 */
export async function destroy<T>(
  url: string,
  config?: ApiRequestConfig
): Promise<ApiResponse<T>> {
  const response = await http.delete<T>(url, config)
  const responseData = response.data as unknown as { value?: T }
  return {
    data:
      responseData && typeof responseData === "object" && "value" in responseData
        ? (responseData.value as T)
        : response.data,
    status: response.status,
    statusText: response.statusText,
  }
}

/**
 * Check if an error is a NormalizedError
 */
export function isNormalizedError(error: unknown): error is NormalizedError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    "message" in error &&
    "fieldErrors" in error
  )
}

/**
 * Extract field error message for a specific field
 */
export function getFieldError(
  error: NormalizedError | null,
  fieldName: string
): string | undefined {
  if (!error?.fieldErrors) return undefined
  const errors = error.fieldErrors[fieldName]
  return errors?.[0]
}

/**
 * Check if error has any field errors
 */
export function hasFieldErrors(error: NormalizedError | null): boolean {
  if (!error?.fieldErrors) return false
  return Object.keys(error.fieldErrors).length > 0
}

// Export the axios instance for direct usage if needed
export { http }

// Default export
export default http
